import {
  formatLength,
  formatLengthAsFractionalFeetAndInches,
  IFormattingSettings,
  LengthUnit,
} from '@fulcrum/lib';
import * as LengthConverter from '../unit-of-measure/length-converter';
import { t } from '@trimble/i18n';
import { BaseFormatterProps } from './base-formatter-props.types';

type Props = BaseFormatterProps<number, LengthUnit, IFormattingSettings> & {
  /**
   * If true, use length measurement settings instead of formatting settings.
   * This maps the length settings as:
   * - lengthMeasurementFormatting <> lengthFormatting
   * - lengthMeasurementUnit <> lengthUnit
   * - lengthMeasurementDecimals <> lengthDecimals
   * - lengthMeasurementFractions <> lengthFractions
   */
  useMeasurementSettings?: boolean;
};

/**
 * The conversion is achieved by first converting to millimeters (mm) as an intermediate step.
 * The 'fulcrum' library uses mm as its base unit for lengths.
 *
 * @remarks Trimble APIs should store and return lengths in meters.
 * @regex ^-?\d+(\.\d+)?\s?(mm|cm|m|in|ft|'|")$
 * - TODO: Refine this regex to better match possible outputs (ft-in notation).
 */
export const toDisplay = ({
  measuredValue,
  currentUnits,
  settings,
  useMeasurementSettings,
  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 19 > 15 allowed; refactor later
}: Props): string => {
  let measuredValueInMillimeters;
  if (currentUnits !== 'mm') {
    // Convert to mm first due to fulcrum lib using mm as base unit
    measuredValueInMillimeters = LengthConverter.mapToMillimeters({
      value: measuredValue,
      baseUnit: currentUnits,
    });
  } else {
    measuredValueInMillimeters = measuredValue;
  }
  // eslint-disable-next-line sonarjs/different-types-comparison -- measuredValue can be null at runtime despite the declared type
  if (measuredValueInMillimeters !== null) {
    if (
      settings.unitSystem === 'imperial' &&
      (useMeasurementSettings
        ? settings.lengthMeasurementFormatting
        : settings.lengthFormatting) === 'fractional'
    ) {
      return formatLengthAsFractionalFeetAndInches(
        measuredValueInMillimeters,
        useMeasurementSettings
          ? settings.lengthMeasurementFractions
          : settings.lengthFractions
      );
    } else {
      return formatLength(
        measuredValueInMillimeters,
        (useMeasurementSettings
          ? settings.lengthMeasurementUnit
          : settings.lengthUnit) ?? 'm',
        useMeasurementSettings
          ? settings.lengthMeasurementDecimals
          : settings.lengthDecimals,
        true
      );
    }
  } else {
    return t('Utilities.Formatting.InvalidUnits', {
      defaultValue: 'Invalid Units',
    });
  }
};
